import { getProfile, profileFromUser } from "misc/profileHelpers";
import { UserManager } from "oidc-client-ts";
import { IntegratedApplicationRecordType } from "pages/IntegratedApplication/IntegratedApplication.types";
import { UserRecordType } from "pages/SharedRecords.types";

import { integratedApplications as integratedApplicationsPath, myProfile as myProfilePath } from "./customPaths";
import { apiEndpoint, apiHost, apiOauthClientId, apiPath } from "./settings";

const OIDC_ISSUER = `${apiHost}${apiPath}/o`;
const OIDC_CLIENT_ID = apiOauthClientId;
const OIDC_REDIRECT_URI = `${window.location.protocol}//${window.location.host}`;
const BACKEND_REDIRECT_URI = apiHost + "/backend/home";
const BACKEND_TWO_FACTOR_REDIRECT = apiHost + "/backend/accounts/two_factor/setup/";
const BACKEND_LOGOUT_REDIRECT = apiHost + "/backend/oauth_logout_redirect/";

export const accessTokenKey = "oauth_access_token";

const userManager = new UserManager({
  authority: OIDC_ISSUER,
  client_id: OIDC_CLIENT_ID,
  redirect_uri: OIDC_REDIRECT_URI,
  response_type: "code",
  scope: "openid read write",
  response_mode: "query",
});

type HttpError = {
  status: number;
};

type LoginParams = {
  code?: string;
  state?: string;
};

function clearStorage() {
  localStorage.removeItem(accessTokenKey);
  localStorage.removeItem("permissions");
  localStorage.removeItem("loginmail");
  localStorage.removeItem("profile");
  localStorage.removeItem("integratedapplications");
  localStorage.removeItem("manageEmailingSenders");
}

export async function fetchProfile(accessToken: string) {
  const url = `${apiEndpoint}${myProfilePath()}`;
  const headers = new Headers({ "Content-Type": "application/json", "Accept-Language": "de" });
  headers.set("X-Authorization", `Bearer ${accessToken}`);
  const request = new Request(url, { headers });

  const response = await fetch(request);

  // The user is authenticated but must still set up/verify 2FA: the backend
  // gates protected endpoints with a 420. Send them to the setup wizard
  if (response.status === 420) {
    clearStorage();
    window.location.href = BACKEND_TWO_FACTOR_REDIRECT;
    throw new Error("Two-factor setup required");
  }

  // An expired token (401) or a server error is not "no login": leave the session to
  // checkError on the next request, which re-logs in silently
  if (!response.ok) {
    throw new Error(`Profile request failed (${response.status})`);
  }

  const body = (await response.json()) as UserRecordType;

  if (!body.login_email) {
    clearStorage();
    window.location.href = BACKEND_REDIRECT_URI;
    throw new Error("User has no login");
  }

  const profile = profileFromUser(body);

  const permissions = {
    groups: body.role_permission_groups || [],
    isSuperuser: body.is_admin || false,
    isStaff: body.is_staff || false,
    isLecturer: body.is_lecturer || false,
    isAttendee: body.is_attendee || false,
    acceptedConsentScopes: body.accepted_consent_scopes || [],
    organizationMemberships: body.organization_memberships || [],
  };

  localStorage.setItem("permissions", JSON.stringify(permissions));
  localStorage.setItem("profile", JSON.stringify(profile));
  localStorage.setItem("loginmail", body.login_email);
  localStorage.setItem("manageEmailingSenders", JSON.stringify(body.manage_emailing_senders));

  return { accessToken, isStaff: body.is_staff || body.is_admin };
}

async function fetchIntegratedApplications(data: string | { accessToken: string; isStaff: boolean }) {
  if (typeof data === "string" || !data.isStaff) {
    return;
  }

  const { accessToken } = data;
  const url = `${apiEndpoint}${integratedApplicationsPath()}`;
  const headers = new Headers({ "Content-Type": "application/json", "Accept-Language": "de" });
  headers.set("X-Authorization", `Bearer ${accessToken}`);
  const request = new Request(url, { headers });

  const response = await fetch(request);
  const body = (await response.json()) as {
    results: IntegratedApplicationRecordType[];
  };

  const preparedIntegratedApplications: { [key: string]: boolean } = {};

  body.results.forEach((integratedApplication) => {
    preparedIntegratedApplications[integratedApplication.slug] = integratedApplication.active;
  });

  localStorage.setItem("integratedapplications", JSON.stringify(preparedIntegratedApplications));
}

const cleanup = () => {
  window.history.replaceState({}, window.document.title, window.location.origin);
};

let isLoggingIn = false;

async function handleLogin(params: LoginParams = {}) {
  if (isLoggingIn) {
    throw new Error("Login already in progress");
  }
  isLoggingIn = true;

  try {
    if (!params || !params.code || !params.state) {
      await userManager.signinRedirect();
      throw new Error("Redirecting to identity provider");
    }

    userManager.clearStaleState();

    const signinResponse = await userManager.signinRedirectCallback();

    if (!signinResponse?.access_token) {
      cleanup();
      throw new Error("No access token received");
    }

    localStorage.setItem(accessTokenKey, signinResponse.access_token);
    cleanup();

    const profileData = await fetchProfile(signinResponse.access_token);
    await fetchIntegratedApplications(profileData);
  } finally {
    isLoggingIn = false;
  }
}

async function handleLogout() {
  // Without a token (e.g. after a 401) only land on the login page, so an
  // expired token can re-login silently instead of ending the backend session
  const wasLoggedIn = !!localStorage.getItem(accessTokenKey);
  clearStorage();
  await userManager.removeUser();
  return wasLoggedIn ? BACKEND_LOGOUT_REDIRECT : undefined;
}

function handleCheckError(error: HttpError) {
  const { status } = error;

  if (status === 420 && localStorage.getItem(accessTokenKey)) {
    clearStorage();
    throw Object.assign(new Error("Two-factor required"), {
      redirectTo: BACKEND_TWO_FACTOR_REDIRECT,
      logoutUser: false,
    });
  }

  if (status === 401) {
    clearStorage();
    throw new Error("Unauthorized");
  }

  if (status === 403) {
    throw Object.assign(new Error("Forbidden"), {
      redirectTo: "/permission_denied",
      logoutUser: false,
    });
  }

  return Promise.resolve();
}

function handleCheckAuth() {
  if (!localStorage.getItem(accessTokenKey)) {
    const error = new Error();
    // react admin best practice: https://marmelab.com/react-admin/AuthProviderWriting.html#checkauth
    (error as unknown as { message: boolean }).message = false;
    throw error;
  }
  return Promise.resolve();
}

function handleGetPermissions() {
  const permissions = localStorage.getItem("permissions");

  if (permissions && permissions.length > 0) {
    return Promise.resolve(JSON.parse(permissions) as Record<string, unknown>);
  }

  return Promise.resolve(null);
}

function handleGetIdentity() {
  const profile = getProfile();

  if (!profile) {
    throw new Error("No profile found");
  }

  const { id, fullName, avatar } = profile;
  return Promise.resolve({ id, fullName, avatar });
}

export default {
  login: (params: LoginParams) => handleLogin(params),
  logout: handleLogout,
  checkAuth: handleCheckAuth,
  checkError: (error: HttpError) => handleCheckError(error),
  getPermissions: handleGetPermissions,
  getIdentity: handleGetIdentity,
};
